import React, { useEffect, useState, forwardRef } from "react";
import "./InputBox.css";
import { useTranslation } from "react-i18next";
import useAppConfigStore from "../../../store/useAppConfigStore";

const InputBox = forwardRef(
  (
    {
      label,
      defaultValue = "",
      onChange,
      onKeyPress,
      onFocus,
      onBlur,
      type = "text",
      style,
      error,
      readOnly = false,
      disabled = false, 
      className = "",
      border = "",
      maxQuantity,
      isPrice = false,
      isNumber = false,
      isPositiveNumber = false,
      isDecimal = false,
      isCoordinate = false,
      important = false,
      maxLength = null,
      maxIntDigits = null,
      autoFocus = false,
      autoComplete = "off",
    },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue ?? "");
    const { t } = useTranslation();
    const decimalPlaces = useAppConfigStore((state) => state.decimalPlaces);

    const handleChange = (event) => {
      if (disabled) return; // ✅ block input if disabled

      let newValue = event.target.value;

      if (maxLength !== null && maxLength !== undefined && Number(maxLength) > 0) {
        if (newValue.length > Number(maxLength)) {
          newValue = newValue.slice(0, Number(maxLength));
        }
      }

      if (
        maxIntDigits !== null &&
        maxIntDigits !== undefined &&
        (isPrice || isDecimal || isCoordinate || isNumber || isPositiveNumber || type === "number")
      ) {
        const intPart = newValue.split(".")[0].replace(/^-/, "");
        if (intPart.length > maxIntDigits) return;
      }

      // Empty allowed (so user can delete & retype)
      if (newValue === "") {
        setValue(newValue);
        if (onChange) onChange(event);
        return;
      }

      // ✅ Handle Coordinate (Latitude & Longitude)
      if (isCoordinate) {
        const regex = /^-?\d*(\.\d*)?$/;
        if (!regex.test(newValue)) return;
      }

      // ✅ Handle Price and Decimal with dynamic decimal places from store
      if (isPrice || isDecimal) {
        // Build regex to allow up to decimalPlaces decimals
        const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);
        if (!regex.test(newValue)) return;
      }

      // ✅ Whole numbers only
      if (isNumber) {
        const regex = /^\d+$/;
        if (!regex.test(newValue)) return;
      }

      // ✅ Positive whole numbers only (allows leading zeros e.g. 00001, validated > 0 on save)
      if (isPositiveNumber) {
        const regex = /^\d+$/;
        if (!regex.test(newValue)) return;
      }

      // ✅ Max quantity check
      if (maxQuantity !== undefined) {
        const regex = /^\d+(\.\d{0,4})?$/;
        if (!regex.test(newValue)) return;
        const numValue = parseFloat(newValue);
        if (parseFloat(maxQuantity) === 0) {
          // Only "0" is allowed if maxQuantity = 0
          if (newValue !== "0") return;
        } else {
          if (numValue > parseFloat(maxQuantity)) return;
        }
      }

      setValue(newValue);
      if (onChange) onChange(event);
    };

    useEffect(() => {
      setValue(defaultValue ?? "");
    }, [defaultValue]);

    const displayErrors = Array.isArray(error) ? error : (error ? [error] : []);

    return (
      <div
        className={`input-box-container posrel ${className} ${disabled ? "disabled" : ""}`}
      >
        <input
          ref={ref}
          className={`input-box common-text ftw-4
                    ${type === "date" ? "input-box-with-icon" : ""}
                    ${displayErrors.length > 0 ? "input-error" : ""}
                    ${border}`}
          style={style}
          type={type}
          value={readOnly && (value === "" || value === null || value === undefined) ? "-" : (value ?? "")}
          onChange={handleChange}
          onKeyPress={onKeyPress}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder=" "
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLength}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
        />
        <label
          className={`input-label common-text ftw-4 ${((value !== undefined && value !== null && value !== "") || (readOnly && (value === "" || value === null || value === undefined))) ? "has-value" : ""}`}
        >
          {t(label)}
          {important && <span className="required-asterisk">*</span>}
        </label>
      </div>
    );
  },
);

// Add display name for better debugging
InputBox.displayName = "InputBox";

export default InputBox;

